import React, { useState, useEffect } from 'react';
import { getBrands, createBrand, updateBrand, deleteBrand, exportBrands } from '../../lib/api';
import { downloadBlob } from '../../lib/downloadHelper';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Loader2,
  AlertCircle,
  Download,
  Printer
} from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const Brands = () => {
  const { isAdmin } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({
    brand_name: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportBrands();
      downloadBlob(res.data, `brands_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Brands exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getBrands();
      setBrands(res.data);
    } catch (error) {
      toast.error('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingBrand(null);
    setFormData({ brand_name: '' });
    setError('');
    setDialogOpen(true);
  };

  const openEditDialog = (brand) => {
    setEditingBrand(brand);
    setFormData({ brand_name: brand.brand_name });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.brand_name) {
      setError('Please enter a brand name');
      return;
    }

    setSaving(true);
    setError('');
    
    try {
      if (editingBrand) {
        await updateBrand(editingBrand.id, formData);
      } else {
        await createBrand(formData);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save brand');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setDeleting(true);
    try {
      await deleteBrand(itemToDelete.id);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete brand');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="brands-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Brands</h1>
          <p className="text-[#6B7280]">Manage product brands</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button onClick={handleExport} disabled={exporting} className="gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg" data-testid="export-brands-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={18} />} Export Excel
          </Button>
          <Button onClick={() => window.print()} variant="outline" className="gap-2 rounded-lg border-[#0776BD] text-[#0776BD]" data-testid="print-brands-btn">
            <Printer size={18} /> Print
          </Button>
          <Button onClick={openCreateDialog} className="gap-2 bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="add-brand-btn">
            <Plus size={18} /> Add Brand
          </Button>
        </div>
      </div>

      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <Building2 size={20} className="text-[#0776BD]" />
            Brands ({brands.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : brands.length === 0 ? (
            <div className="py-12 text-center text-[#6B7280]">
              <Building2 size={48} className="mx-auto mb-4 opacity-50" />
              <p>No brands found</p>
              <p className="text-sm">Click "Add Brand" to create one</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F6F7]">
                  <TableHead className="font-semibold text-[#1F2937]">Brand Name</TableHead>
                  <TableHead className="font-semibold text-[#1F2937] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brands.map((brand) => (
                  <TableRow key={brand.id} className="hover:bg-[#F5F6F7]" data-testid={`brand-row-${brand.id}`}>
                    <TableCell className="font-medium text-[#1F2937]">{brand.brand_name}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(brand)}
                          className="hover:bg-[#E6F2FA] text-[#0776BD]"
                          data-testid={`edit-brand-${brand.id}`}
                        >
                          <Edit size={18} />
                        </Button>
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setItemToDelete(brand); setDeleteDialogOpen(true); }}
                            className="hover:bg-red-50 text-red-500"
                            data-testid={`delete-brand-${brand.id}`}
                          >
                            <Trash2 size={18} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">
              {editingBrand ? 'Edit Brand' : 'Add Brand'}
            </DialogTitle>
          </DialogHeader>
          
          {error && (
            <Alert variant="destructive" className="rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Brand Name *</Label>
              <Input
                value={formData.brand_name}
                onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                placeholder="e.g., Leviton"
                className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="brand-name-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="save-brand-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingBrand ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">Delete Brand</DialogTitle>
          </DialogHeader>
          <p className="text-[#6B7280]">Are you sure you want to delete "{itemToDelete?.brand_name}"? This may affect items using this brand.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="rounded-lg" data-testid="confirm-delete-brand-btn">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Brands;
